import { Link } from 'react-router-dom';
import type { Article } from '@/types/content';

interface ArticleCardProps {
  article: Article;
  variant?: 'feature' | 'default' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const href = `/article/${article.id}`;

  if (variant === 'compact') {
    return (
      <Link
        to={href}
        className="group flex items-start gap-4 border-b border-background-200/70 py-4 transition-colors hover:bg-background-100/60"
      >
        <span className="font-heading flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary-100 text-sm font-bold text-primary-800">
          {article.heat >= 8000 ? '热' : '料'}
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-medium leading-snug text-foreground-950 group-hover:text-primary-700 md:text-base">
            {article.title}
          </h3>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-3 text-xs text-foreground-500">
            <span>{article.categoryName}</span>
            <span>{article.date}</span>
            <span>{article.readTime}</span>
          </p>
        </div>
      </Link>
    );
  }

  if (variant === 'feature') {
    return (
      <Link
        to={href}
        className="group relative flex h-full min-h-[380px] flex-col justify-end overflow-hidden rounded-lg"
      >
        <div className="absolute inset-0 h-full w-full">
          <img
            src={article.cover}
            alt={`${article.title} - 爆料看料深度拆解配图`}
            title={`${article.title} - 爆料看料`}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/10" />
        </div>
        <div className="relative z-10 p-6 md:p-8">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
            <i className="ri-fire-line" />
            今日热扒
          </span>
          <h3 className="font-heading mt-4 text-xl font-bold leading-snug text-background-50 md:text-2xl">
            {article.title}
          </h3>
          <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-background-200">{article.excerpt}</p>
          <p className="mt-4 flex flex-wrap items-center gap-x-4 text-xs text-background-300">
            <span>{article.author}</span>
            <span>{article.date}</span>
            <span>{article.readTime}</span>
          </p>
        </div>
      </Link>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={href} className="block h-48 w-full overflow-hidden">
        <img
          src={article.cover}
          alt={`${article.title} - 深度拆解配图`}
          title={`${article.title} - 爆料看料`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
            {article.categoryName}
          </span>
          <span className="text-xs text-foreground-500">{article.readTime}</span>
        </div>
        <h3 className="font-heading mt-3 text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link to={href} className="hover:text-primary-700">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.excerpt}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-4">
          <span className="text-xs text-foreground-500">
            {article.author} · {article.date}
          </span>
          <span className="flex items-center gap-1 text-xs text-primary-700">
            阅读拆解
            <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </article>
  );
}